import { Alert, AlertTitle, Box, IconButton, Paper, useTheme } from '@mui/material';
import { Modal } from '@mui/material';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
  getAnnotationToolAdminProjectsPath,
  useAppDispatch,
} from '@verantos-dev/core-annotation-tool';
import { Icon } from '@verantos-dev/shared-components';

import { CREATE_PROJ_SUCCESS_MODAL_TIMEOUT } from '../../constants';
import { clearAllWizardStates } from '../../state/createProjectSlice';

interface CreateProjectSuccessModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  projName: string;
}

export const CreateProjectSuccessModal = ({
  showModal,
  setShowModal,
  projName,
}: CreateProjectSuccessModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    dispatch(clearAllWizardStates());
    navigate(getAnnotationToolAdminProjectsPath());
  }, [setShowModal, dispatch, navigate]);

  const [seconds, setSeconds] = useState(CREATE_PROJ_SUCCESS_MODAL_TIMEOUT);

  useEffect(() => {
    if (!showModal) return;

    setSeconds(CREATE_PROJ_SUCCESS_MODAL_TIMEOUT);

    const interval = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    const timeout = setTimeout(() => {
      handleCloseModal();
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [showModal, handleCloseModal]);

  return (
    <Modal
      open={showModal}
      onClose={handleCloseModal}
      aria-labelledby="finalized-annotation-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          px: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: theme.spacing(3),
          }}
        >
          <Typography id="finalized-annotation-modal-title" variant="h6">
            {formatMessage({
              id: 'createProjectWizard.step.reviewAndCreate.createSuccessModal.title',
            })}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <Icon id="create-proj-success-modal-close-icon" icon="clear" width={16} />
          </IconButton>
        </Box>
        <Alert severity="success" sx={{ border: `1px solid ${theme.palette.success.main}` }}>
          <AlertTitle>
            {formatMessage({
              id: 'createProjectWizard.step.reviewAndCreate.createSuccessModal.alert.title',
            })}
          </AlertTitle>
          <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
            {formatMessage(
              {
                id: 'createProjectWizard.step.reviewAndCreate.createSuccessModal.alert.description',
              },
              { projName: projName }
            )}
          </Typography>
        </Alert>
        <Typography
          variant="body1"
          sx={{ py: theme.spacing(3), fontSize: '12px', color: theme.palette.grey[600] }}
        >
          {formatMessage(
            { id: 'createProjectWizard.step.reviewAndCreate.createSuccessModal.close.message' },
            { seconds }
          )}
        </Typography>
      </Paper>
    </Modal>
  );
};
